import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  UPDATEREPORT,
  ADDREPORT,
  GETREPORTBYID,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";

const getSchema = () =>
  z.object({
    title: z.string({ required_error: "Title is required" }),
    description: z.string({ required_error: "Description is required" }),
    status: z.enum(["active", "inactive"]),
  });

const AddReport = () => {
  const [reportDetails, setReportDetails] = useState();
  const { id } = useParams();
  const schema = useMemo(() => getSchema(), []);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      status: "active",
      description: "",
    },
    values: {
      title: reportDetails?.title || "",
      status: reportDetails?.status === "active",
      description: reportDetails?.description || "",
    },
  });

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      ...(id && { _id: id }),
      status: data.status ? "active" : "inactive",
    };


    const res = await (id ? UPDATEREPORT(body) : ADDREPORT(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate("/report");
    }

  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await GETREPORTBYID({ _id: id });
    checkResponse({
      res,
      setData: setReportDetails,
      setLoader,
    });
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => {
    navigate("/report");
  };

  return (
    <>
      {loader && <Loader />}
      <section className="addReport position-relative py-3">
        <Container>
          <Row className="align-items-center mb-3 d-flex flex-column flex-md-row">
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/report"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#1E232C"
                      stroke="#1E232C"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  {id ? "Edit Report" : "Add Report"}
                </h4>
              </div>
            </Col>

            <Row className="justify-content-center w-100">
              <Col lg="6" className="my-2">
                <div
                  className="formWrpper px-lg-5 p-md-4 rounded"
                  style={{ border: "1px solid #dee2e6" }}
                >
                  <Form onSubmit={handleSubmit(submitHandler)}>
                    <Row className="g-3">
                      <Col lg="12" className="my-2">
                        <div className="py-2">
                          <label className="form-label fw-sbold text-muted ps-2 m-0">
                            Title
                          </label>
                          <input
                            type="text"
                            placeholder="Enter report title"
                            className="form-control"
                            {...register("title")}
                          />
                          {errors?.title && (
                            <p className="text-danger m-0">
                              {errors?.title?.message}
                            </p>
                          )}
                        </div>
                      </Col>
                      <Col lg="12" className="my-2">
                        <div className="py-2">
                          <label className="form-label fw-sbold text-muted ps-2 m-0">
                            Description
                          </label>
                          <textarea
                            rows={8}
                            className="form-control h-auto"
                            {...register("description")}
                          ></textarea>
                          {errors?.description && (
                            <p className="text-danger m-0">
                              {errors?.description?.message}
                            </p>
                          )}
                        </div>
                      </Col>
                      <Col lg="12" className="my-2">
                        <div className="py-2">
                          <label className="form-label fw-sbold text-muted ps-2 m-0">
                            Status
                          </label>
                          <Form.Check
                            type="switch"
                            id="custom-switch"
                            {...register("status")}
                          />
                          {errors?.status && (
                            <p className="text-danger m-0">
                              {errors?.status?.message}
                            </p>
                          )}
                        </div>
                      </Col>

                      <Col lg="12" className="my-2">
                        <div className="d-flex align-items-center justify-content-center gap-10">
                          <Button
                            type="button"
                            onClick={handleCancel}
                            className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="d-flex align-items-center justify-content-center commonBtn"
                          >
                            Submit
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </Col>
            </Row>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddReport;
